/* MUI */
import { Avatar, AvatarGroup, Box, Typography } from '@mui/material';

/* Shared */
import {
    LightTooltip,
    TooltipPlacement
} from 'application/Shared/Components/Tooltips';

/* Types */
import { User, UserFullName, UserIcon } from 'objects/User';
import React from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderUser = <TData,>({
    data,
    showText,
    sx
}: {
    data: TData;
    showText?: boolean;
    sx?: React.CSSProperties;
}) => (
    // @ts-expect-error using generic TData
    <User data={data} key={`avatar_${data.id}`}>
        <Box
            data-testid="userAvatarWrapper"
            display="flex"
            my={showText ? '4px' : 0}
            sx={{ ...sx }}
            alignItems="center"
        >
            <UserIcon noLink />
            {showText && (
                <Box ml="8px">
                    <UserFullName isTooltipText link noTruncate />
                </Box>
            )}
        </Box>
    </User>
);

export const UserBubbles = <TData,>({
    align,
    dataIsUser,
    max,
    tooltipPlacement,
    users,
    userCount
}: {
    align?: string;
    dataIsUser?: boolean;
    max?: number;
    tooltipPlacement?: TooltipPlacement;
    users: TData[];
    userCount?: number;
}) => {
    if (!users || !Array.isArray(users)) {
        return null;
    }

    const sortedUsers = users.sort((a: TData, b: TData) => {
        // @ts-expect-error I'm using a generic TData
        if (a.user?.serialId && b.user?.serialId) {
            // @ts-expect-error I'm using a generic TData
            return a.user.serialId - b.user.serialId;
        }
        return 0;
    });

    // If userCount is provided, we might have a subset of these users -- figure out how
    // many users are "missing" so we can indicate how many additional users there are
    const totalUsers = userCount || sortedUsers.length;
    const missingUsers = totalUsers - sortedUsers.length;

    return (
        <Box
            className="usersAvatarWrapper"
            data-testid="usersAvatarWrapper"
            display="flex"
            style={{
                justifyContent: align === 'right' ? 'flex-end' : 'flex-start'
            }}
        >
            <LightTooltip
                title={
                    <Box maxHeight="160px" overflow="auto">
                        {sortedUsers.map((data) => {
                            // @ts-expect-error I'm using a generic TData
                            const user = dataIsUser ? data : data.user;
                            if (user) {
                                return renderUser({
                                    data: user,
                                    showText: true,
                                    sx: { marginRight: '-4px' }
                                });
                            }
                            return null;
                        })}
                        {missingUsers > 0 && (
                            <User key="avatar_missing">
                                <Box ml="8px">
                                    +{missingUsers} additional users
                                </Box>
                            </User>
                        )}
                    </Box>
                }
                arrow
                placement={
                    tooltipPlacement || (align === 'left' ? 'right' : 'left')
                }
            >
                <AvatarGroup
                    data-testid="usersAvatarGroup"
                    max={max}
                    className="no-print"
                >
                    {sortedUsers.map((data) => {
                        // @ts-expect-error I'm using a generic TData
                        const user = dataIsUser ? data : data.user;
                        if (user) {
                            return renderUser({
                                data: user,
                                sx: { marginRight: '-4px' }
                            });
                        }
                        return null;
                    })}
                    {missingUsers > 0 &&
                        Array.from({ length: missingUsers }).map((_, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <Avatar key={`missing-${index}`} />
                        ))}
                </AvatarGroup>
            </LightTooltip>
            {/* When printed we only want to show count of Users */}
            <Typography className="print-only">{totalUsers}</Typography>
        </Box>
    );
};
